import { Popover } from '@mui/material';
import classnames from 'classnames';
import { forwardRef, useContext, useImperativeHandle, useMemo, useRef, useState } from 'react';

import { extractTrigger } from '../../Popover/Popover.types';

import styles from './Menu.module.scss';
import { MenuActions, menuContext, MenuProps } from './Menu.types';

export const Menu = forwardRef<MenuActions, MenuProps>(
  ({ trigger, disabled, className, onOpen, onClose, slotProps, ...props }, ref) => {
    const outerMenu = useContext(menuContext);

    const [isOpen, setIsOpen] = useState(false);

    const menuActions = useMemo(
      (): MenuActions => ({
        open: () => setIsOpen(true),
        close: () => setIsOpen(false),
      }),
      []
    );

    useImperativeHandle(ref, () => menuActions, [menuActions]);

    const triggerRef = useRef<HTMLSpanElement | null>(null);

    return (
      <menuContext.Provider value={outerMenu ?? menuActions}>
        <span
          className={styles.trigger}
          data-triggered={isOpen && !disabled ? 'triggered' : ''}
          onClick={() => {
            if (disabled) return;
            setIsOpen(true);
            onOpen?.();
          }}
          ref={triggerRef}
        >
          {trigger}
        </span>
        {isOpen && !disabled && (
          <Popover
            anchorEl={extractTrigger(triggerRef.current)}
            anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
            transformOrigin={{ horizontal: 'left', vertical: 'top' }}
            open
            onClose={(event, reason) => {
              setIsOpen(false);
              onClose?.(event, reason);
            }}
            onClick={event => event.stopPropagation()}
            onMouseDown={event => event.stopPropagation()}
            onMouseUp={event => event.stopPropagation()}
            slotProps={{
              ...slotProps,
              paper: { ...slotProps?.paper, className: classnames(styles.menu, className) },
            }}
            {...props}
          />
        )}
      </menuContext.Provider>
    );
  }
);
